import { PaymentActivity } from 'further-types/lp/bank-reconciliation';
import { CurrencyCode } from 'further-types/lp/vehicle';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import { numberToCurrencyString } from 'further-ui/utils';
import api from 'lib/trpcClient';
import { ReactNode } from 'react';

type Props = {
  currencyCode: CurrencyCode;
  vehicleId: string;
  amountTcy: number;
  transactionId?: string;
  title?: ReactNode;
  hidePayments?: (
    payments: Array<{
      activity: PaymentActivity;
      amountTcy: number;
    }>,
    amountTcy: number,
  ) => boolean;
  darkBackground?: boolean;
};

const ReviewPayments: React.FC<Props> = ({
  currencyCode,
  vehicleId,
  transactionId,
  amountTcy,
  hidePayments,
  title,
  darkBackground = false,
}: Props) => {
  if (!transactionId) {
    return null;
  }

  const { data: payments, isLoading } =
    api.prepayments.paymentsPerApportionmentPeriod.useQuery({
      transactionId,
      vehicleId,
    });

  if (isLoading) {
    return null;
  }

  if (hidePayments?.(payments ?? [], amountTcy)) {
    return null;
  }

  const rows = (payments ?? []).map((payment, index) => ({
    id: index.toString(),
    ...payment,
  }));

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'date',
      label: 'Date',
      render: (row) => row.date.toReadable(),
    },
    {
      key: 'activityLabel',
      label: 'Activity',
      render: (row) => row.activityLabel,
    },
    {
      key: 'amountTcy',
      label: 'Amount',
      render: (row) =>
        numberToCurrencyString(row.amountTcy, {
          currency: currencyCode,
        }),
    },
    {
      key: 'amountOutstandingTcy',
      label: 'Amount outstanding',
      render: (row) =>
        row.amountOutstandingTcy !== undefined
          ? numberToCurrencyString(row.amountOutstandingTcy, {
              currency: currencyCode,
            })
          : '',
    },
  ];

  return (
    <Table
      title={title}
      columns={columns}
      rows={rows}
      darkBackground={darkBackground}
    />
  );
};

export default ReviewPayments;
